import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Printer, FileText, Bell, AlertTriangle, User, Trash2, GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_META = {
  citatorio_profesor: { label: 'Citatorio (Profesor)', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Bell },
  aviso_profesor: { label: 'Aviso (Profesor)', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Bell },
  citatorio_ce: { label: 'Citatorio (Control Escolar)', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Bell },
  aviso_ce: { label: 'Aviso (Control Escolar)', color: 'bg-sky-100 text-sky-700 border-sky-300', icon: Bell },
  reporte_prefectura: { label: 'Reporte de Prefectura', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
};

const GRAVEDAD_META = {
  leve: { label: 'Leve', dot: 'bg-green-500', badge: 'bg-green-100 text-green-700 border-green-200' },
  moderado: { label: 'Moderado', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  grave: { label: 'Grave', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

// Reusable Historial Conducta section (search + results + detail).
// Used standalone by the Orientador page and embedded inside Grupo Tutorado (teacher).
const HistorialConductaSection = ({ showHeader = true, canDelete = false }) => {
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/conducta/student/${selected.id}`, authHeaders());
      toast.success('Historial de conducta borrado');
      setDeleteOpen(false);
      setSelected(null);
      setEntries([]);
      setResults(null);
      setTerm('');
    } catch (e) {
      toast.error('Error al borrar el historial');
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = async () => {
    const q = term.trim();
    if (!q) { toast.error('Escribe el nombre o matrícula del alumno'); return; }
    setSearching(true);
    setSelected(null);
    setEntries([]);
    try {
      const res = await axios.get(`${API}/conducta/search-students`, { ...authHeaders(), params: { name: q } });
      setResults(res.data.students || []);
      if (!res.data.found) toast.info('No se encontraron alumnos');
    } catch (e) {
      toast.error('Error al buscar');
    } finally {
      setSearching(false);
    }
  };

  const loadHistory = async (student) => {
    setSelected(student);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conducta/student/${student.id}`, authHeaders());
      setEntries(res.data.entries || []);
      setGrades(res.data.grades || []);
    } catch (e) {
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="space-y-6" data-testid="historial-conducta-page">
      {showHeader && (
        <div className="flex items-center gap-3 no-print">
          <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Historial Conducta</h1>
            <p className="text-sm text-slate-500">Historial global de citatorios, avisos y reportes por alumno (solo lectura)</p>
          </div>
        </div>
      )}

      <Card className="p-4 no-print">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o matrícula..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent pl-10 pr-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
              data-testid="conducta-search-input"
            />
          </div>
          <Button onClick={handleSearch} disabled={searching} data-testid="conducta-search-btn" className="bg-slate-800 hover:bg-slate-900">
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>

        {results && results.length > 0 && !selected && (
          <div className="mt-4 space-y-2" data-testid="conducta-results">
            {results.map((s) => (
              <button
                key={s.id}
                onClick={() => loadHistory(s)}
                data-testid={`conducta-student-${s.id}`}
                className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <User className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="font-semibold text-slate-800">{s.name}</p>
                  <p className="text-xs text-slate-500">
                    {s.matricula ? `Matrícula: ${s.matricula}` : 'Sin matrícula'}
                    {s.grupo ? ` · Grupo: ${s.grupo}` : ''}{s.turno ? ` · ${s.turno}` : ''}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
        {results && results.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">Sin resultados.</p>
        )}
      </Card>

      {selected && (() => {
        const gravCounts = entries.reduce((acc, en) => {
          if (en.tipo === 'reporte_prefectura') {
            const g = (en.detalle?.gravedad || 'leve').toLowerCase();
            acc[g] = (acc[g] || 0) + 1;
          }
          return acc;
        }, {});
        const gravTotal = (gravCounts.leve || 0) + (gravCounts.moderado || 0) + (gravCounts.grave || 0);
        return (
        <Card className="p-6" data-testid="conducta-detail" id="printable-conducta">
          <div className="flex items-start justify-between border-b border-slate-200 pb-4 mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{selected.name}</h2>
              <p className="text-sm text-slate-500">
                {selected.matricula ? `Matrícula: ${selected.matricula}` : 'Sin matrícula'}
                {selected.grupo ? ` · Grupo: ${selected.grupo}` : ''}{selected.turno ? ` · Turno: ${selected.turno}` : ''}
              </p>
            </div>
            <div className="flex gap-2 no-print">
              <Button variant="outline" onClick={() => { setSelected(null); setEntries([]); }} data-testid="conducta-back-btn">Volver</Button>
              {canDelete && (
                <Button onClick={() => setDeleteOpen(true)} data-testid="conducta-delete-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="w-4 h-4 mr-2" /> Borrar historial
                </Button>
              )}
              <Button onClick={handlePrint} data-testid="conducta-print-btn" className="bg-slate-800 hover:bg-slate-900">
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
            </div>
          </div>

          {gravTotal > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="conducta-gravedad-summary">
              <span className="text-xs font-semibold text-slate-500">Reportes de Prefectura:</span>
              {['leve', 'moderado', 'grave'].map((g) => {
                const meta = GRAVEDAD_META[g];
                return (
                  <span key={g} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold ${meta.badge}`} data-testid={`conducta-gravedad-count-${g}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                    {meta.label}: {gravCounts[g] || 0}
                  </span>
                );
              })}
            </div>
          )}
          {loading ? (
            <p className="text-slate-500">Cargando...</p>
          ) : entries.length === 0 ? (
            <p className="text-slate-500" data-testid="conducta-empty">Este alumno no tiene registros de conducta.</p>
          ) : (
            <div className="space-y-3">
              {entries.map((en) => {
                const meta = TIPO_META[en.tipo] || { label: en.tipo, color: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText };
                const Icon = meta.icon;
                const d = en.detalle || {};
                return (
                  <div key={en.id} className="border border-slate-200 rounded-lg p-4" data-testid={`conducta-entry-${en.id}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <span className="text-xs text-slate-500">{fmtDate(en.fecha)}</span>
                    </div>
                    <div className="text-sm text-slate-700 space-y-1">
                      {d.materia && <p><span className="font-semibold">Materia:</span> {d.materia}</p>}
                      {d.motivo && <p><span className="font-semibold">Motivo:</span> {d.motivo}</p>}
                      {d.mensaje && <p><span className="font-semibold">Mensaje:</span> {d.mensaje}</p>}
                      {d.descripcion && <p><span className="font-semibold">Descripción:</span> {d.descripcion}</p>}
                      {(d.hora || d.lugar) && (
                        <p className="text-xs text-slate-500">{d.hora ? `Hora: ${d.hora}` : ''}{d.hora && d.lugar ? ' · ' : ''}{d.lugar ? `Lugar: ${d.lugar}` : ''}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">Emitido por: {en.emisor_name || '—'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Permanent grades concentrate (parciales por materia/semestre) */}
          {!loading && (
          <div className="mt-6 pt-4 border-t border-slate-200" data-testid="conducta-grades-section">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-slate-500" /> Concentrado de Calificaciones
            </h3>
            {grades.length === 0 ? (
              <p className="text-slate-500 text-sm" data-testid="conducta-grades-empty">Aún no hay calificaciones parciales cerradas para este alumno.</p>
            ) : (
              <div className="space-y-4">
                {Array.from(new Set(grades.map(g => (g.semester != null ? g.semester : '—')))).map((sem) => (
                  <div key={String(sem)} data-testid={`grades-semester-${sem}`}>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                      {sem === '—' ? 'Sin semestre' : `${sem}° Semestre`}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="text-left px-3 py-2 font-medium">Materia</th>
                            <th className="text-center px-3 py-2 font-medium w-24">Parcial 1</th>
                            <th className="text-center px-3 py-2 font-medium w-24">Parcial 2</th>
                            <th className="text-center px-3 py-2 font-medium w-24">Parcial 3</th>
                            <th className="text-center px-3 py-2 font-bold w-24 bg-slate-100">Promedio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grades.filter(g => (g.semester != null ? g.semester : '—') === sem).map((g, gi) => {
                            const byP = {};
                            (g.parciales || []).forEach(p => { byP[p.parcial] = p.promedio; });
                            const cell = (v) => v == null ? <span className="text-slate-300">—</span> :
                              <span className={`font-bold ${v >= 6 ? 'text-emerald-600' : 'text-red-600'}`}>{Number(v).toFixed(1)}</span>;
                            const vals = [byP[1], byP[2], byP[3]].filter(v => v != null).map(Number);
                            const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length) : null;
                            return (
                              <tr key={gi} className="border-t border-slate-100" data-testid={`grades-row-${sem}-${gi}`}>
                                <td className="px-3 py-2 text-slate-700">{g.subject}</td>
                                <td className="px-3 py-2 text-center">{cell(byP[1])}</td>
                                <td className="px-3 py-2 text-center">{cell(byP[2])}</td>
                                <td className="px-3 py-2 text-center">{cell(byP[3])}</td>
                                <td className="px-3 py-2 text-center bg-slate-50" data-testid={`grades-avg-${sem}-${gi}`}>{cell(avg)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </Card>
        );
      })()}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Borrar historial de conducta
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Se eliminará <strong>todo el historial de conducta</strong> de{' '}
            <strong>{selected?.name}</strong>, incluyendo los citatorios y avisos de profesores y
            Control Escolar, y los reportes de Prefectura que lo originaron. Las justificaciones del
            orientador <strong>se conservan</strong>. Esta acción no se puede deshacer.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="conducta-delete-cancel">Cancelar</Button>
            <Button onClick={handleDelete} disabled={deleting} data-testid="conducta-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? 'Borrando...' : 'Sí, borrar historial'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistorialConductaSection;
